/**
 * 🎯 Per-creature on-use skills (v2.86) — the second half of the creature
 * expansion's core layer.
 *
 * These REPLACE Surge rather than adding to it: a creature naming one
 * here is `noSurge`, so `creatureSkill()` returns kind 'none' and the
 * roster shows this button in the Surge slot instead.
 *
 * Two shapes, and the distinction drives all the plumbing:
 *
 *   instant  fires the moment it is clicked (Graft, Bloom). Some need a
 *            target, which puts the roster into pick-a-teammate mode.
 *   primed   arms and waits for the creature's next dispatch (Skim,
 *            Overheat), exactly like a Surge — it is consumed when the
 *            mission resolves, win or lose.
 *
 * Cooldowns reuse the Surge convention: a value above ONCE_PER_NODE
 * means "spent, recharges when the node ends", which the roster already
 * renders as ⌛ Used.
 */
export const ONCE_PER_NODE = 9999

export interface CreatureSkillDef {
  id: string
  name: string
  icon: string
  /** Player-facing effect text. */
  blurb: string
  kind: 'instant' | 'primed'
  /** Seconds before it can fire again; ONCE_PER_NODE = once a node. */
  cooldown: number
  /** Instant skills that ask the player to pick a teammate first. */
  needsTarget?: boolean
  /** 🎨 Painted medallion (v3.14) — injected below, one per skill. */
  image?: string
}

export const CREATURE_SKILLS: Record<string, CreatureSkillDef> = {
  // ⚖️ v5.95: Legacy's on-use (per Javi: 10s CD, runs out after
  // mission end — the revert lives in balanceOfAgesExpire).
  'balance-of-ages': {
    id: 'balance-of-ages',
    name: 'Balance of Ages',
    icon: '⚖️',
    blurb: 'Evens its stats into a level spread until its next mission ends.',
    kind: 'instant',
    cooldown: 10,
  },
  // 🎩 v6.88: Mr. Khool's on-use (settled 2026-09-10). Primed like Skim:
  // it rides the mission he is dispatched to; the maths live in
  // utils/battle (wagerSteps / wagerPurse / wagerDamageMult).
  // 🎲 v7.79 (per Javi: "30sec cooldown and success should be a flat amount of gold and XP rather than %. That way a
  // non-gold mission rewards some and also make it every 5% missing instead"): 30 s from resolution, like Skim; a
  // flat purse per 5% step on any mission (CREATURE_SKILL_VALUES.wager*).
  'the-wager': {
    id: 'the-wager',
    name: 'The Wager',
    icon: '🎲',
    blurb: 'Rides his next mission. For every 5% its locked chance sits below 100, a win pays a flat +1 gold and +½ XP each (more in later acts), and a loss costs +10% damage.',
    kind: 'primed',
    cooldown: 30,
  },
  // 🚃 v6.89: Aid's on-use (settled 2026-09-10, corrected by Javi: it
  // does NOT auto-win). Instant, once a node; the hurry lives in
  // utils/battle (useCarriage).
  'the-carriage': {
    id: 'the-carriage',
    name: 'The Carriage',
    icon: '🚃',
    blurb: 'Every mission out in the field resolves this instant, at the chance it already locked in, and every team is home at once.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
  // 🕊️ v6.90: The Noble One's on-use (settled 2026-09-10). The one button
  // he gives you is the one that costs you everything you were saving.
  'let-go': {
    id: 'let-go',
    name: 'Let Go',
    icon: '🕊️',
    blurb: 'Spends every stack of Silence: each becomes a permanent point, dealt around the roster onto lowest elements.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
  // 🌀 v6.91: Lanfeust's on-use (Javi's design, settled 2026-09-10: it pays
  // nothing - the stolen statline is the whole payment). Armed like a
  // Decree: the next eligible dispatch is eaten (utils/battle).
  blackhole: {
    id: 'blackhole',
    name: 'Blackhole',
    icon: '🌀',
    blurb: 'Arm it, then dispatch him: that mission is swallowed on the spot, no roll, no travel, no wait, and pays no XP, gold or goal credit. He keeps its requirements as bonus stats for his next deployment.',
    kind: 'primed',
    cooldown: ONCE_PER_NODE,
  },
  // 🎯 v6.92: Robodude's on-use (settled 2026-09-10). He shells the fight
  // next door from where he is parked; nobody travels to him.
  'fire-support': {
    id: 'fire-support',
    name: 'Fire Support',
    icon: '🎯',
    blurb: 'From where he stands, unassigned: the live mission closest to him counts his full statline without taking a slot, until it resolves.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
  graft: {
    id: 'graft',
    name: 'Graft',
    icon: '🌱',
    blurb: "Permanently moves 1 point from its highest element to a teammate's lowest.",
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
    needsTarget: true,
  },
  skim: {
    id: 'skim',
    name: 'Skim',
    icon: '💧',
    blurb: 'Its next mission costs no travel time at all — out and back.',
    kind: 'primed',
    cooldown: 30,
  },
  bloom: {
    id: 'bloom',
    name: 'Bloom',
    icon: '🌸',
    blurb: 'Heals you its LEVEL in HP and mends one injury on your most injured creature.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
  overheat: {
    id: 'overheat',
    name: 'Overheat',
    icon: '🔥',
    blurb: 'Its next mission gains +12 🔥 — but it cannot be dispatched again this node.',
    kind: 'primed',
    cooldown: ONCE_PER_NODE,
  },
  tithe: {
    id: 'tithe',
    name: 'Tithe',
    icon: '🪙',
    blurb: 'Pay 5 gold — the next mission dispatched gains +20% success chance.',
    kind: 'instant',
    cooldown: 30,
  },
  'bulwark-drop': {
    id: 'bulwark-drop',
    name: 'Bulwark Drop',
    icon: '🧱',
    blurb: 'The next failed mission anywhere deals 0 damage and causes no injuries.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
  refract: {
    id: 'refract',
    name: 'Refract',
    icon: '🔷',
    blurb: "Copies a chosen teammate's full statline onto itself for its next mission.",
    kind: 'instant',
    cooldown: 30,
    needsTarget: true,
  },
  plunder: {
    id: 'plunder',
    name: 'Plunder',
    icon: '🏴‍☠️',
    blurb: 'Converts all gold above 50 into +1 permanent point of its highest element AND its lowest above 0, per 10 gold spent.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
  deluge: {
    id: 'deluge',
    name: 'Deluge',
    icon: '🌊',
    blurb: 'Every waiting mission gains +25% success chance for 20 seconds.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
  foresight: {
    id: 'foresight',
    name: 'Foresight',
    icon: '🔭',
    // v4.66 (per Javi): pick ONE creature — the field rerolls to match it.
    blurb: 'Pick a creature: REROLLS the requirements of every waiting mission to lean hard into ITS elements.',
    kind: 'instant',
    needsTarget: true,
    // v4.27 (per Javi): the reroll pulls much harder now, and pays for
    // it — 30s → 60s.
    cooldown: 60,
  },
  bulwark: {
    id: 'bulwark',
    name: 'Bulwark',
    icon: '🛡️',
    blurb: 'Arm it: its next mission deals ZERO damage even on failure and causes no injuries — then the Sentinel is spent for 20 seconds.',
    kind: 'primed',
    cooldown: ONCE_PER_NODE,
  },
  luminesce: {
    id: 'luminesce',
    name: 'Luminesce',
    icon: '✨',
    blurb: "Arm it: if its next mission succeeds, the node's next reward pick offers 4 cards.",
    kind: 'primed',
    cooldown: ONCE_PER_NODE,
  },
  reforge: {
    id: 'reforge',
    name: 'Reforge',
    icon: '⚒️',
    blurb: "Permanently redistributes a teammate's entire statline however you choose.",
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
    needsTarget: true,
  },
  mimic: {
    id: 'mimic',
    name: 'Mimic',
    icon: '🎭',
    blurb: "Copies a CHOSEN teammate's Surge onto itself for its next mission — a Dual Surge is copied at FULL strength on both elements.",
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
    needsTarget: true,
  },
  decree: {
    id: 'decree',
    name: 'Decree',
    icon: '👑',
    blurb: 'Arm it, then Fight any waiting non-boss, non-gold mission: it WINS instantly and pays full XP — no roll, no travel, no wait.',
    kind: 'instant',
    cooldown: ONCE_PER_NODE,
  },
}

/** Numeric knobs, so a balance pass is a data edit. */
export const CREATURE_SKILL_VALUES = {
  /** 🌱 Graft: points moved per use. */
  graftPoints: 1,
  /** 🌸 Bloom: HP restored to the player (v3.18: 6 → 5, per Javi). */
  /** 🔥 Overheat: 🔥 added to its next mission. */
  overheatFire: 12,
  /** 🪙 Tithe: the gold it costs, and the chance it buys. */
  titheCost: 5,
  titheChance: 20,
  /** 🏴‍☠️ Plunder: gold kept / gold per permanent point. */
  plunderFloor: 50,
  plunderRate: 10,
  /** 🌊 Deluge: the chance boost and how long the window stays open. */
  delugeChance: 25,
  delugeSeconds: 20,
  /** 🛡️ Bulwark: seconds the Sentinel is spent after its shield fires. */
  bulwarkLockout: 20,
  /** 🎲 The Wager (v7.79): one step per this many % the locked chance sits below 100. */
  wagerStepPct: 5,
  /** 🎲 A won bet's purse per step, by act (index act-1; later acts use the last): gold to the wallet... */
  wagerGoldPerStep: [1, 2, 3],
  /** ...and XP to each teammate (half the act's mission-XP multiplier 1 / 1.4 / 1.8; rounded once per bet). */
  wagerXpPerStep: [0.5, 0.7, 0.9],
  /** 🎲 A lost bet's damage: +10% per step (the old +20% per 10%). */
  wagerDamagePerStep: 0.1,
} as const

export function getCreatureSkillDef(id: string | undefined): CreatureSkillDef | null {
  return id ? CREATURE_SKILLS[id] ?? null : null
}

// 🎨 Painted icons (v3.14, per Javi: "icons rather than emojis").
// ?v=2: v4.61's full icon repaint (art by Javi) — all 15 replaced in place.
// 🔢 Per-id revs (v6.30): Legacy's medallion is Javi's painted triangle
// now, replacing the placeholder v5.95 shipped with. Bumping ONE id
// leaves the other fifteen in everyone's cache — a blanket ?v=3 would
// re-download all sixteen for one repaint.
const ICON_REV: Record<string, number> = { 'balance-of-ages': 3 }
/** 🎨 v6.88: medallions Javi has not painted yet - these render their
 * emoji (the art rule: a placeholder, never generated art). Add an id
 * here for a new skill until /ui/cskills/<id>.webp lands; v7.24 emptied
 * it (the five homage medallions arrived). */
export const PLACEHOLDER_SKILL_ICONS = new Set<string>([])
for (const def of Object.values(CREATURE_SKILLS)) {
  if (PLACEHOLDER_SKILL_ICONS.has(def.id)) continue
  def.image = `/ui/cskills/${def.id}.webp?v=${ICON_REV[def.id] ?? 2}`
}
